import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  paymentApi,
  type PaymentListParams,
  type RefundListParams,
  type CreateRefundInput,
  type DisputeListParams,
  type ReconciliationListParams,
  type DailyCashListParams,
  type CallbackListParams,
} from "@/lib/admin-api/payment";
export type { ReconciliationListParams } from "@/lib/admin-api/payment";

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const KEYS = {
  kpis: ["admin-payment-kpis"] as const,
  transactions: (params: PaymentListParams) =>
    ["admin-payment-transactions", params] as const,
  refunds: (params: RefundListParams) =>
    ["admin-payment-refunds", params] as const,
  disputes: (params: DisputeListParams) =>
    ["admin-payment-disputes", params] as const,
  reconciliation: (params: ReconciliationListParams) =>
    ["admin-payment-reconciliation", params] as const,
  dailyCash: (params: DailyCashListParams) =>
    ["admin-payment-daily-cash", params] as const,
  codTrustScores: ["admin-payment-cod-trust-scores"] as const,
} as const;

// ---------------------------------------------------------------------------
// Transactions
// ---------------------------------------------------------------------------

export function usePaymentKPIs() {
  return useQuery({
    queryKey: KEYS.kpis,
    queryFn: () => paymentApi.transactions.getKPIs(),
    staleTime: 60_000,
  });
}

export function usePaymentTransactions(params: PaymentListParams = {}) {
  return useQuery({
    queryKey: KEYS.transactions(params),
    queryFn: () => paymentApi.transactions.list(params),
    staleTime: 30_000,
  });
}

export function useConfirmPayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => paymentApi.transactions.confirm(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-payment-transactions"] });
      queryClient.invalidateQueries({ queryKey: KEYS.kpis });
    },
  });
}

export function useRefundPayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => paymentApi.transactions.refund(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-payment-transactions"] });
      queryClient.invalidateQueries({ queryKey: KEYS.kpis });
    },
  });
}

// ---------------------------------------------------------------------------
// Refunds
// ---------------------------------------------------------------------------

export function useRefunds(params: RefundListParams = {}) {
  return useQuery({
    queryKey: KEYS.refunds(params),
    queryFn: () => paymentApi.refunds.list(params),
    staleTime: 30_000,
  });
}

export function useCreateRefund() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateRefundInput) =>
      paymentApi.refunds.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-payment-refunds"] });
      queryClient.invalidateQueries({ queryKey: KEYS.kpis });
    },
  });
}

export function useApproveRefund() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => paymentApi.refunds.approve(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-payment-refunds"] });
      queryClient.invalidateQueries({ queryKey: KEYS.kpis });
    },
  });
}

export function useRejectRefund() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { readonly id: string; readonly reason: string }) =>
      paymentApi.refunds.reject(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-payment-refunds"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Disputes
// ---------------------------------------------------------------------------

export function useDisputes(params: DisputeListParams = {}) {
  return useQuery({
    queryKey: KEYS.disputes(params),
    queryFn: () => paymentApi.disputes.list(params),
    staleTime: 30_000,
  });
}

export function useResolveDispute() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      resolution,
    }: {
      readonly id: string;
      readonly resolution: "merchant" | "customer";
    }) => paymentApi.disputes.resolve(id, resolution),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-payment-disputes"] });
      queryClient.invalidateQueries({ queryKey: KEYS.kpis });
    },
  });
}

export function useInvestigateDispute() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => paymentApi.disputes.investigate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-payment-disputes"] });
    },
  });
}

export function useDisputeDetail(id: string | null) {
  return useQuery({
    queryKey: ["admin-payment-dispute-detail", id] as const,
    queryFn: () => paymentApi.disputes.getDetail(id!),
    enabled: !!id,
    staleTime: 15_000,
  });
}

export function useAddDisputeNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, message }: { readonly id: string; readonly message: string }) =>
      paymentApi.disputes.addNote(id, message),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-payment-dispute-detail"] });
      queryClient.invalidateQueries({ queryKey: ["admin-payment-disputes"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Reconciliation
// ---------------------------------------------------------------------------

export function useReconciliation(params: ReconciliationListParams = {}) {
  return useQuery({
    queryKey: KEYS.reconciliation(params),
    queryFn: () => paymentApi.reconciliation.list(params),
    staleTime: 60_000,
  });
}

export function useTriggerReconciliation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (date: string) => paymentApi.reconciliation.trigger(date),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin-payment-reconciliation"],
      });
    },
  });
}

export function useExportReconciliationCsv() {
  return useMutation({
    mutationFn: (params: ReconciliationListParams) =>
      paymentApi.reconciliation.exportCsv(params),
  });
}

// ---------------------------------------------------------------------------
// Daily cash
// ---------------------------------------------------------------------------

export function useDailyCash(params: DailyCashListParams = {}) {
  return useQuery({
    queryKey: KEYS.dailyCash(params),
    queryFn: () => paymentApi.dailyCash.list(params),
    staleTime: 30_000,
  });
}

export function useValidateCashSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => paymentApi.dailyCash.validate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-payment-daily-cash"] });
    },
  });
}

export function useFlagCashSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { readonly id: string; readonly reason: string }) =>
      paymentApi.dailyCash.flag(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-payment-daily-cash"] });
    },
  });
}

// ---------------------------------------------------------------------------
// COD Trust Scores
// ---------------------------------------------------------------------------

export function useCODTrustScores() {
  return useQuery({
    queryKey: KEYS.codTrustScores,
    queryFn: () => paymentApi.cod.trustScores(),
    staleTime: 120_000,
  });
}

// ---------------------------------------------------------------------------
// Callback logs
// ---------------------------------------------------------------------------

export function useCallbackLogsQuery(params?: CallbackListParams) {
  return useQuery({
    queryKey: ["admin-callback-logs", params] as const,
    queryFn: () => paymentApi.callbacks.list(params ?? {}),
    staleTime: 30_000,
  });
}

export function useCallbackDetailQuery(id: string | null | undefined) {
  return useQuery({
    queryKey: ["admin-callback-detail", id] as const,
    queryFn: () => paymentApi.callbacks.getDetail(id!),
    enabled: !!id,
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Dead letter queue
// ---------------------------------------------------------------------------

export function useDeadLetterQuery() {
  return useQuery({
    queryKey: ["admin-dead-letters"] as const,
    queryFn: () => paymentApi.deadLetter.list(),
    staleTime: 30_000,
  });
}

export function useRetryDeadLetter() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => paymentApi.deadLetter.retry(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-dead-letters"] });
    },
  });
}

export function useDismissDeadLetter() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => paymentApi.deadLetter.dismiss(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-dead-letters"] });
    },
  });
}
